import React, { useRef, useState, useMemo, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useIMask } from 'react-imask';

import { useStateBasedOnProps } from 'utils/hooks';

import FieldLabel from 'components/FieldLabel';

interface FieldDefaultProps {
    type?: 'text' | 'number',
    value?: string,
    id?: string,
    name?: string,
    placeholder?: string,
    label?: string,
    labelHelp?: string,
    darkLabel?: boolean,
    largeLabel?: boolean,
    minLength?: number,
    maxLength?: number,
    required?: boolean,
    replacer?: (value: string) => string,
    triggerAsEvent?: boolean,
    blurOnEnter?: boolean,
    autoFocus?: boolean,
    disabled?: boolean,
    shortField?: boolean,
    error?: boolean,
    small?: boolean,
    large?: boolean,
    readonly?: boolean,
    sideAction?: React.ReactNode,
    passwordToggleable?: boolean,
    addon?: React.ReactNode,
    addonTransparent?: boolean,
    mask?: string,
    provideController?: any,
    onChange?: (value: string) => void,
    onFocus?: () => void,
    onBlur?: (value: string) => void,
    onKeyDown?: () => void,
    onClick?: () => void,
    onInput?: () => void,
    onPaste?: () => void,
    onSideActionClick?: () => void,
    addonClick?: () => void,
    children?: React.ReactNode
}

const FieldDefault = ({ type = 'text', value = '', id, name, placeholder, label, labelHelp, darkLabel, largeLabel, minLength, maxLength, required, replacer, triggerAsEvent, blurOnEnter, autoFocus, disabled, shortField, error, small, large, readonly, sideAction, passwordToggleable, addon, addonTransparent, mask, provideController, onChange, onFocus, onBlur, onKeyDown, onClick, onInput, onPaste, onSideActionClick, addonClick, children }: FieldDefaultProps) => {
    const { ref: inputElement, maskRef, value: maskedValue } = useIMask({ mask });
    const [valueState, setValueState] = useStateBasedOnProps(value);
    const [focused, setFocused] = useState(false);
    const [passwordVisible, setPasswordVisible] = useState(false);
    const { t } = useTranslation('common');

    const populated = useMemo(() => valueState !== '', [valueState]);

    const errorComputed = useMemo(() => {
        if (error) {
            return true;
        }

        if (typeof minLength !== 'undefined' && valueState?.length < minLength) {
            return true;
        }

        return false;
    }, [error, minLength, valueState?.length]);

    const labelElement = useMemo(() => {
        if (!label) {
            return null;
        }

        return <FieldLabel
            id={id}
            dark={darkLabel}
            large={largeLabel}
            required={required}
            help={labelHelp}
        >{ label }</FieldLabel>;
    }, [id, label, labelHelp, darkLabel, largeLabel, required]);

    const getValue = useCallback(() => {
        const rawValue = inputElement.current?.value;

        if (replacer) {
            return replacer(rawValue);
        }

        return rawValue;
    }, [inputElement, replacer]);

    const focus = useCallback(() => {
        inputElement.current?.focus();
    }, [inputElement]);

    const handleChange = useCallback(event => {
        if (mask) {
            return;
        }

        const value = getValue();

        if (maxLength && value.length > maxLength) {
            return;
        }

        setValueState(value);

        onChange && onChange(triggerAsEvent ? event : value);
    }, [mask, getValue, triggerAsEvent, onChange]);

    const handleFocus = useCallback(() => {
        setFocused(true);

        onFocus && onFocus();
    }, [onFocus]);

    const handleBlur = useCallback(() => {
        setFocused(false);

        onBlur && onBlur(inputElement.current?.value);
    }, [onBlur, inputElement]);

    const handleKeyDown = useCallback(event => {
        if (blurOnEnter && event.which === 13) {
            event.preventDefault();
            inputElement.current?.blur();
        }

        onKeyDown && onKeyDown(event);
    }, [blurOnEnter, inputElement, onKeyDown]);

    const handleTogglePassword = useCallback(() => {
        setPasswordVisible(prevState => !prevState);
    }, []);

    useEffect(() => {
        if (autoFocus) {
            focus();
        }
    }, []);

    useEffect(() => {
        if (mask) {
            onChange?.(maskedValue);
        }
    }, [mask, maskedValue, onChange]);

    useEffect(() => {
        const ctrl = {
            focus: () => {
                focus();
            },
            getValue: () => {
                return valueState;
            },
            setValue: value => {
                setValueState(value);
            },
            isPopulated: () => {
                return populated;
            }
        };

        if (provideController) {
            if ('current' in provideController) {
                provideController.current = ctrl;
            } else {
                provideController && provideController(ctrl);
            }
        }

        return () => {
            if (provideController) {
                if ('current' in provideController) {
                    provideController.current = null;
                } else {
                    provideController && provideController(null);
                }
            }
        }
    }, [provideController, valueState, populated]);

    return (
        <div className="field-holder">
            { labelElement }

            <div className={'field field-default ' + ('field-type-' + type) + (disabled ? ' disabled' : '') + (populated ? ' populated' : '') + (focused ? ' focus' : '') + (shortField ? ' field-short' : '') + (errorComputed ? ' error' : '') + (small ? ' field-small' : '') + (large ? ' field-large' : '')} onClick={onClick}>

                { children }

                <input
                    className="field-input"
                    ref={inputElement}
                    type={passwordVisible ? 'tyext' : type}
                    readOnly={readonly}
                    name={name}
                    value={valueState}
                    id={id}
                    placeholder={placeholder}
                    onKeyDown={handleKeyDown}
                    onInput={onInput}
                    onFocus={handleFocus}
                    onBlur={handleBlur}
                    onChange={handleChange}
                    onPaste={onPaste}
                    disabled={disabled}
                />

                {
                    typeof minLength !== 'undefined' || maxLength ? (
                        <span className="field-min-max-length">
                            {
                                typeof minLength !== 'undefined' && valueState?.length < minLength ?
                                    `${minLength} characters minimum`
                                :
                                    maxLength ? `${valueState?.length}/${maxLength}` : ''
                            }
                        </span>
                    ) : null
                }

                {
                    sideAction ? (
                        <span className="field-side-action" onClick={onSideActionClick}>
                            { sideAction }
                        </span>
                    ) : null
                }

                {
                    passwordToggleable ? (
                        <span className="toggle-password" onClick={handleTogglePassword}>
                            <span className="hidden-state">{ t('show') }</span>

                            <span className="visible-state">{ t('hide') }</span>
                        </span>
                    ) : null
                }

                {
                    addon ? (
                        <span className={'field-input-addon' + (addonTransparent ? ' transparent' : '')} onClick={addonClick}>
                            { addon }
                        </span>
                    ) : null
                }

            </div>
        </div>
    );
}


export default FieldDefault;
